import { Button, Card, CircularProgress, Grid } from "@mui/material";
import { type DateRange } from "@mui/x-date-pickers-pro";
import { DateRangePickerButton } from "@packsize/components";
import { DateTime } from "luxon";
import { useIntl } from "react-intl";
import { useEffect, useState } from "react";

import { type AuditRecord, DiffView } from "@/components/diff-view";
import { FailedRetry } from "@/components/failed-retry";
import useAxios from "@/api/use-axios";
import { baseRoutes } from "@/api/base-routes";
import { ConfigurationPageLayout } from "@/views/configuration/components/configuration-page-layout";

import { sanitizeConfigurationAuditData } from "../../../utils/sanitize-audit-data";

export const ConfigurationAudit = () => {
	const intl = useIntl();
	const configurationAuditApi = useAxios(`${baseRoutes.audit}/v1/ConfigurationAudit`);
	const [loading, setLoading] = useState(true);
	const [failed, setFailed] = useState(false);
	const [list, setList] = useState<AuditRecord[]>([]);

	const [dateRange, setDateRange] = useState<DateRange<DateTime>>([DateTime.utc().minus({ days: 7 }), DateTime.utc()]);

	const load = () => {
		setFailed(false);
		setLoading(true);
		setList([]);

		if (dateRange[0] == null || dateRange[1] == null) {
			return;
		}

		configurationAuditApi.getWithUrl(
			`byStartDateUtcEpoch/${dateRange[0].toUnixInteger()}/byEndDateUtcEpoch/${dateRange[1].toUnixInteger()}`,
			(data) => {
				setList(data as AuditRecord[]);
				setLoading(false);
			},
			() => {
				setLoading(false);
				setFailed(true);
			},
		);
	};

	useEffect(load, []);

	const localizedRedacted = intl.formatMessage({ id: "Redacted", defaultMessage: "Redacted" });

	return (
		<ConfigurationPageLayout
			title={intl.formatMessage({ id: "Configuration Audit", defaultMessage: "Configuration Audit" })}
			actions={
				<>
					<DateRangePickerButton
						sx={{ width: "300px", backgroundColor: "common.white" }}
						shortcuts={["today", "yesterday", "last7Days", "last30Days"]}
						maxDate={DateTime.now()}
						closeOnSelect={false}
						onChange={(val) => setDateRange([val[0] ?? dateRange[0], val[1] ?? dateRange[1]])}
						onAccept={(val) => setDateRange([val[0] ?? dateRange[0], val[1] ?? dateRange[1]])}
						value={dateRange}
					/>

					<Button
						variant="contained"
						loading={loading}
						onClick={load}
						data-pendoid="index__button__configuration-audit"
					>
						{intl.formatMessage({ id: "Load", defaultMessage: "Load" })}
					</Button>
				</>
			}
		>
			<Card sx={{ overflow: "auto" }}>
				{failed === true && <FailedRetry retry={load} />}

				{loading === true && (
					<Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 400 }}>
						<CircularProgress color="info" data-testid="configuration-audit-loader" />
					</Grid>
				)}

				{loading === false && failed === false && (
					<DiffView
						data={list}
						compareAll
						additionalDataTransform={(data) => sanitizeConfigurationAuditData(data, localizedRedacted)}
						actionColumn={undefined}
					/>
				)}
			</Card>
		</ConfigurationPageLayout>
	);
};
